'use client'

import React, { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, Search, X } from 'lucide-react'
import { cn } from './utils'
import { FloatingPortal } from './FloatingPortal'

export interface DropdownOption {
  value: string
  label: string
  icon?: React.ReactNode
}

export interface DropdownProps {
  options: DropdownOption[]
  value?: string
  onChange: (value: string) => void
  placeholder?: string
  label?: string
  error?: string
  searchable?: boolean
  clearable?: boolean
  disabled?: boolean
  className?: string
  animate?: boolean
}

export const Dropdown: React.FC<DropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  label,
  error,
  searchable = false,
  clearable = true,
  disabled,
  className,
  animate = true
}) => {
  const shouldAnimate = animate !== false
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [dropdownPlacement, setDropdownPlacement] = useState<'top' | 'bottom'>('bottom')
  const dropdownRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const ignoreNextClickRef = useRef(false)

  const selectedOption = options.find((opt) => opt.value === value)

  useEffect(() => {
    if (!isOpen || !searchable) return

    const focusSearchInput = window.setTimeout(() => {
      searchInputRef.current?.focus()
    }, 0)

    return () => window.clearTimeout(focusSearchInput)
  }, [isOpen, searchable])
  
  const filteredOptions = searchable
    ? options.filter((opt) =>
        opt.label.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : options

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchQuery('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    window.addEventListener('keydown', handleEscape)

    const updatePlacement = () => {
      if (!triggerRef.current) return

      const rect = triggerRef.current.getBoundingClientRect()
      const estimatedMenuHeight = searchable ? 360 : 300
      const availableBelow = window.innerHeight - rect.bottom - 8
      const availableAbove = rect.top - 8

      if (availableBelow >= estimatedMenuHeight) {
        setDropdownPlacement('bottom')
        return
      }

      if (availableAbove >= estimatedMenuHeight) {
        setDropdownPlacement('top')
        return
      }

      setDropdownPlacement('bottom')
    }

    updatePlacement()
    window.addEventListener('resize', updatePlacement)
    window.addEventListener('scroll', updatePlacement, true)

    return () => {
      window.removeEventListener('keydown', handleEscape)
      window.removeEventListener('resize', updatePlacement)
      window.removeEventListener('scroll', updatePlacement, true)
    }
  }, [isOpen, searchable])

  const handleSelect = (optionValue: string) => {
    onChange(optionValue)
    setIsOpen(false)
    setSearchQuery('')
  }

  const dropdownAnimation =
    dropdownPlacement === 'top'
      ? { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: 10 } }
      : { initial: { opacity: 0, y: -10 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -10 } }

 
  return (
    <div ref={dropdownRef} className={cn('relative w-full', className)}>
      {label && (
        <label className="block mb-1.5 text-sm font-medium text-foreground">
          {label}
        </label>
      )}

      <button
        ref={triggerRef}
        type="button"
        onMouseDown={(event) => {
          if (!searchable || disabled || isOpen) return
          event.preventDefault()
          ignoreNextClickRef.current = true
          setIsOpen(true)
        }}
        onClick={() => {
          if (ignoreNextClickRef.current) {
            ignoreNextClickRef.current = false
            return
          }
          if (!disabled) {
            setIsOpen((current) => !current)
          }
        }}
        disabled={disabled}
        className={cn(
          'w-full px-4 py-3 rounded-md border transition-all duration-200',
          'bg-background text-left flex items-center justify-between gap-2',
          'focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/30',
          'disabled:bg-secondary disabled:cursor-not-allowed',
          'touch-target',
          error && 'border-error',
          !error && 'border-border',
          isOpen && 'ring-2 ring-primary'
        )}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {selectedOption?.icon && <span className="shrink-0">{selectedOption.icon}</span>}
          <span className={cn('truncate', !selectedOption && 'text-foreground/50')}>
            {selectedOption?.label || placeholder}
          </span>
        </div>
        <ChevronDown
          className={cn(
            'w-5 h-5 text-primary transition-transform shrink-0',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {shouldAnimate ? (
        <AnimatePresence>
          {isOpen ? (
            <FloatingPortal open={isOpen} anchorRef={triggerRef} placement={dropdownPlacement}>
              <motion.div
                initial={dropdownAnimation.initial}
                animate={dropdownAnimation.animate}
                exit={dropdownAnimation.exit}
                transition={{ duration: 0.2 }}
                className="z-50 w-full bg-background rounded-md border border-border shadow-lg max-h-80 overflow-hidden"
              >
              {searchable && (
                <div className="p-2 border-b border-border">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary/50" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Escape') {
                          setIsOpen(false)
                        }
                      }}
                      className="w-full pl-10 pr-8 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/30"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2"
                      >
                        <X className="w-4 h-4 text-primary/50 hover:text-primary" />
                      </button>
                    )}
                  </div>
                </div>
              )}

              <div className="overflow-y-auto max-h-64 p-2 space-y-1">
                {clearable && (
                  <button
                    type="button"
                    onClick={() => handleSelect('')}
                    className={cn(
                      'w-full px-4 py-3 text-left flex items-center gap-2 rounded-sm cursor-pointer',
                      'hover:bg-secondary transition-colors',
                      'text-foreground/70 italic',
                      !value && 'bg-border text-foreground'
                    )}
                  >
                    <span>None</span>
                  </button>
                )}
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleSelect(option.value)}
                      className={cn(
                        'w-full px-4 py-3 text-left flex items-center gap-2 rounded-sm cursor-pointer',
                        'hover:bg-secondary transition-colors',
                        option.value === value && 'bg-border text-foreground'
                      )}
                    >
                      {option.icon && <span className="shrink-0">{option.icon}</span>}
                      <span>{option.label}</span>
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-6 text-center text-primary/70 text-sm">
                    No options found
                  </div>
                )}
              </div>
              </motion.div>
            </FloatingPortal>
          ) : null}
        </AnimatePresence>
      ) : (
        isOpen && (
          <FloatingPortal open={isOpen} anchorRef={triggerRef} placement={dropdownPlacement}>
            <motion.div className="z-50 w-full bg-background rounded-md border border-border shadow-lg max-h-80 overflow-hidden">
            {searchable && (
              <div className="p-2 border-b border-border">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary/50" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Escape') {
                        setIsOpen(false)
                      }
                    }}
                    className="w-full pl-10 pr-8 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/30"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2"
                    >
                      <X className="w-4 h-4 text-primary/50 hover:text-primary" />
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className="overflow-y-auto max-h-64 p-2 space-y-1">
              {clearable && (
                <button
                  type="button"
                  onClick={() => handleSelect('')}
                  className={cn(
                    'w-full px-4 py-3 text-left flex items-center gap-2 rounded-sm cursor-pointer',
                    'hover:bg-secondary transition-colors',
                    'text-foreground/70 italic',
                    !value && 'bg-border text-foreground'
                  )}
                >
                  <span>None</span>
                </button>
              )}
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleSelect(option.value)}
                    className={cn(
                      'w-full px-4 py-3 text-left flex items-center gap-2 rounded-sm cursor-pointer',
                      'hover:bg-secondary transition-colors',
                      option.value === value && 'bg-border text-foreground'
                    )}
                  >
                    {option.icon && <span className="shrink-0">{option.icon}</span>}
                    <span>{option.label}</span>
                  </button>
                ))
              ) : (
                <div className="px-4 py-6 text-center text-primary/70 text-sm">
                  No options found
                </div>
              )}
            </div>
            </motion.div>
          </FloatingPortal>
        )
      )}

      {error && (
        <motion.p
          {...(shouldAnimate ? { initial: { opacity: 0, y: -5 }, animate: { opacity: 1, y: 0 } } : {})}
          className="mt-1.5 text-sm text-error"
        >
          {error}
        </motion.p>
      )}
    </div>
  )
}
